import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Input, Typography } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { formatAuthError } from '../utils/authErrors';

/* v2 mark (magnifying glass over a green/teal/blue skyline — see
   eco-build-portfolio/brand/sp_mark_v2.svg for the full master), full colour, for this light
   card background. */
const BrandMark: React.FC<{ size?: number }> = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 128 128" aria-hidden="true">
    <defs>
        <clipPath id="spm-lens">
          <circle cx="74" cy="60" r="26.5" />
        </clipPath>
      </defs>

      <line x1="2" y1="92" x2="126" y2="92" stroke="#5A6166" strokeWidth="2.4" strokeLinecap="round" />

      <g>
        <rect x="25.2" y="78" width="2.2" height="14" fill="#5A6166" />
        <circle cx="22" cy="74" r="5.6" fill="#4E9151" />
        <circle cx="27.4" cy="71.6" r="6.4" fill="#4E9151" />
        <circle cx="23.6" cy="67.8" r="4.8" fill="#4E9151" />
      </g>

      <g>
        <rect x="57" y="16" width="22" height="76" fill="#6BA5A8" />
        <rect x="62" y="22" width="4.5" height="4.5" fill="#A9CDCF" />
        <rect x="70" y="22" width="4.5" height="4.5" fill="#A9CDCF" />
        <rect x="62" y="30" width="4.5" height="4.5" fill="#A9CDCF" />
        <rect x="70" y="30" width="4.5" height="4.5" fill="#A9CDCF" />

        <rect x="86" y="32" width="16" height="60" fill="#BCD4E3" />
        <rect x="89" y="40" width="10" height="2.4" fill="#93B4C9" />
        <rect x="89" y="47" width="10" height="2.4" fill="#93B4C9" />
        <rect x="89" y="54" width="10" height="2.4" fill="#93B4C9" />

        <rect x="45" y="34" width="14" height="58" fill="#74B968" />
        <rect x="48" y="40" width="3.5" height="3.5" fill="#A6D79C" />
        <rect x="53.5" y="40" width="3.5" height="3.5" fill="#A6D79C" />
        <rect x="48" y="48" width="3.5" height="3.5" fill="#A6D79C" />
        <rect x="53.5" y="48" width="3.5" height="3.5" fill="#A6D79C" />

        <rect x="32" y="58" width="15" height="34" fill="#6BA5A8" />
        <rect x="35.5" y="63" width="3.5" height="3.5" fill="#A9CDCF" />
        <rect x="41" y="63" width="3.5" height="3.5" fill="#A9CDCF" />
        <rect x="35.5" y="70" width="3.5" height="3.5" fill="#A9CDCF" />
        <rect x="41" y="70" width="3.5" height="3.5" fill="#A9CDCF" />

        <rect x="100" y="60" width="14" height="32" fill="#74B968" />
        <rect x="103" y="66" width="3.5" height="3.5" fill="#A6D79C" />
        <rect x="108.5" y="66" width="3.5" height="3.5" fill="#A6D79C" />
        <rect x="103" y="74" width="3.5" height="3.5" fill="#A6D79C" />
        <rect x="108.5" y="74" width="3.5" height="3.5" fill="#A6D79C" />

        <rect x="72" y="52" width="15" height="40" fill="#2E4FA8" />
        <rect x="75" y="58" width="9" height="2.6" fill="#7E9DD9" />
        <rect x="75" y="64.5" width="9" height="2.6" fill="#7E9DD9" />
        <rect x="75" y="71" width="9" height="2.6" fill="#7E9DD9" />
        <rect x="75" y="77.5" width="9" height="2.6" fill="#7E9DD9" />
      </g>

      <g clipPath="url(#spm-lens)">
        <rect x="44" y="30" width="62" height="64" fill="#F4F8F4" />

        <rect x="57" y="16" width="22" height="76" fill="#9CC4C6" />
        <rect x="62" y="30" width="4.5" height="4.5" fill="#CFE4E5" />
        <rect x="70" y="30" width="4.5" height="4.5" fill="#CFE4E5" />

        <rect x="86" y="32" width="16" height="60" fill="#D3E3EE" />
        <rect x="89" y="40" width="10" height="2.4" fill="#A9C4D6" />
        <rect x="89" y="47" width="10" height="2.4" fill="#A9C4D6" />
        <rect x="89" y="54" width="10" height="2.4" fill="#A9C4D6" />
        <rect x="89" y="61" width="10" height="2.4" fill="#A9C4D6" />
        <rect x="89" y="68" width="10" height="2.4" fill="#A9C4D6" />

        <rect x="45" y="34" width="14" height="58" fill="#A8D49F" />
        <rect x="48" y="48" width="3.5" height="3.5" fill="#F4F8F4" />
        <rect x="53.5" y="48" width="3.5" height="3.5" fill="#F4F8F4" />
        <rect x="48" y="56" width="3.5" height="3.5" fill="#F4F8F4" />
        <rect x="53.5" y="56" width="3.5" height="3.5" fill="#F4F8F4" />
        <rect x="48" y="64" width="3.5" height="3.5" fill="#F4F8F4" />
        <rect x="53.5" y="64" width="3.5" height="3.5" fill="#F4F8F4" />
        <rect x="48" y="72" width="3.5" height="3.5" fill="#F4F8F4" />
        <rect x="53.5" y="72" width="3.5" height="3.5" fill="#F4F8F4" />

        <rect x="100" y="60" width="14" height="32" fill="#A8D49F" />
        <rect x="103" y="66" width="3.5" height="3.5" fill="#F4F8F4" />
        <rect x="108.5" y="66" width="3.5" height="3.5" fill="#F4F8F4" />

        <rect x="72" y="52" width="15" height="40" fill="#2E4FA8" />
        <rect x="75" y="58" width="9" height="2.6" fill="#8AA6DD" />
        <rect x="75" y="64.5" width="9" height="2.6" fill="#8AA6DD" />
        <rect x="75" y="71" width="9" height="2.6" fill="#8AA6DD" />
        <rect x="75" y="77.5" width="9" height="2.6" fill="#8AA6DD" />
        <rect x="75" y="84" width="9" height="2.6" fill="#8AA6DD" />
      </g>

      <line x1="53" y1="81" x2="21" y2="113" stroke="#ffffff" strokeWidth="20" strokeLinecap="round" />
      <circle cx="74" cy="60" r="31.5" fill="none" stroke="#ffffff" strokeWidth="8" />
      <line x1="53" y1="81" x2="21" y2="113" stroke="#5A6166" strokeWidth="14" strokeLinecap="round" />
      <circle cx="74" cy="60" r="29" fill="none" stroke="#5A6166" strokeWidth="5" />
  </svg>
);

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try { setError(''); setLoading(true); await resetPassword(email); setMessage('Check your email for reset instructions.'); }
    catch (err) { setError(formatAuthError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#ECEFF1', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <Card style={{ width: 400, maxWidth: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <BrandMark />
          <Typography.Title level={4} style={{ fontFamily: 'Poppins, sans-serif', margin: '8px 0 2px', color: '#263238' }}>
            mep<span style={{ color: '#4CAF50' }}>-tools</span>
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12, letterSpacing: '.6px', textTransform: 'uppercase' }}>
            by Sustainability-Pros
          </Typography.Text>
          <div style={{ marginTop: 8 }}>
            <Typography.Text type="secondary">Reset your password</Typography.Text>
          </div>
        </div>
        {message && <Alert type="success" message={message} showIcon style={{ marginBottom: 16 }} />}
        {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}
        <form onSubmit={handleSubmit}>
          <Input
            type="email"
            size="large"
            prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.35)' }} />}
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoFocus
            style={{ marginBottom: 16 }}
          />
          <Button type="primary" htmlType="submit" block size="large" loading={loading}>
            {loading ? 'Sending...' : 'Send Reset Email'}
          </Button>
        </form>
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13 }}>
          <Link to="/login">Back to login</Link>
        </div>
      </Card>
    </div>
  );
};

export default ForgotPassword;
